// Family — adults-only oversight of the kids' LLM chat. Per-kid cards with
// the parent controls (on/off, daily limit, quiet hours, persona) plus a
// read-only chat review (transcripts + safety flags). Opened from the user
// menu; only meaningful for adults who have kids in their household.
//
// Settings autosave per-field (on change for toggles, on blur for inputs).
// Phase 1: the controls + review surface. Kid chat itself + flag generation
// land in Phases 2 + 3 — this screen already reads whatever exists.

import { useEffect, useState } from "preact/hooks";
import "../../styles/family.css";

import { Icon } from "../../icons";
import { api, type KidSummary, type KidChatSettings, type KidChatSessionSummary, type KidChatFlag } from "../../lib/rpc";
import type { NotifyFn } from "../../components/toast";
import type { Me } from "../../lib/use-me";
import { SettingsHouseholds } from "../settings/households";

interface FamilyViewProps {
  open: boolean;
  onClose: () => void;
  me: Me | null;
  notify: NotifyFn;
}

export function FamilyView({ open, onClose, me, notify }: FamilyViewProps) {
  const [kids, setKids] = useState<KidSummary[] | null>(null);
  const [reviewKid, setReviewKid] = useState<string | null>(null);

  const refresh = () => {
    api.kidsList().then(setKids).catch((e: any) => { notify(e?.message || "couldn't load kids"); setKids([]); });
  };

  useEffect(() => { if (open) { setReviewKid(null); refresh(); } }, [open]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") (reviewKid ? setReviewKid(null) : onClose()); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose, reviewKid]);

  if (!open) return null;

  // Optimistic local patch after a successful save so the card reflects it
  // without a full refetch.
  const applyLocal = (kidUserId: string, settings: KidChatSettings) =>
    setKids((prev) => prev ? prev.map((k) => k.kidUserId === kidUserId ? { ...k, settings } : k) : prev);

  return (
    <div className="family-bg" onClick={onClose}>
      <div className="family-sheet" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Family">
        <header className="family-head">
          <div className="family-head-left">
            <span className="family-head-badge"><Icon name="people" size={17} /></span>
            <div>
              <h2 className="family-head-title">Family</h2>
              <div className="family-head-meta">Members, kids &amp; their apps</div>
            </div>
          </div>
          <button className="icon-btn" onClick={onClose} title="Close" type="button"><Icon name="x" /></button>
        </header>

        <div className="family-body">
          {reviewKid ? (
            <KidReview kidUserId={reviewKid} notify={notify} onBack={() => { setReviewKid(null); refresh(); }} />
          ) : (
            <>
              {/* Household management (members, add kid, switch, etc.) lives
                  here now — the one hub for everything family. */}
              <SettingsHouseholds me={me} notify={notify} />

              {/* Per-kid app + chat controls. */}
              <section className="family-kids">
                <div className="family-section-label">Kids' apps &amp; chat</div>
                {kids === null && <p className="family-loading">Loading…</p>}
                {kids !== null && kids.length === 0 && (
                  <div className="family-kids-empty">No kids yet — add one above, then their app controls show here.</div>
                )}
                {kids?.map((k) => (
                  <KidCard key={k.kidUserId} kid={k} notify={notify} onSaved={applyLocal} onReview={() => setReviewKid(k.kidUserId)} />
                ))}
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function KidCard({ kid, notify, onSaved, onReview }: {
  kid: KidSummary; notify: NotifyFn;
  onSaved: (kidUserId: string, s: KidChatSettings) => void;
  onReview: () => void;
}) {
  const [s, setS] = useState<KidChatSettings>(kid.settings);
  const [saving, setSaving] = useState(false);
  const [feat, setFeat] = useState(kid.features);

  // Toggle a non-chat surface (rewards/calendar/todo/journal) — optimistic.
  const toggleFeature = async (f: keyof typeof feat, enabled: boolean) => {
    const before = feat;
    setFeat({ ...feat, [f]: enabled });
    try { await api.kidsSetFeature(kid.kidUserId, f, enabled); }
    catch (e: any) { setFeat(before); notify(e?.message || "couldn't save"); }
  };

  // Local-only field edits (keeps controlled inputs current while typing so a
  // sibling field's save doesn't clobber in-progress text).
  const patchLocal = (patch: Partial<KidChatSettings>) => setS((cur) => ({ ...cur, ...patch }));

  // Persist a field. Optimistic-merge only — we deliberately do NOT overwrite
  // local state with the server echo, so a save on one field can't wipe text
  // a user is still typing in another. Revert just the attempted patch on error.
  const save = async (patch: Partial<KidChatSettings>) => {
    const before = s;
    setSaving(true);
    setS((cur) => ({ ...cur, ...patch }));
    try {
      const saved = await api.kidsSetSettings(kid.kidUserId, patch);
      onSaved(kid.kidUserId, saved);
    } catch (e: any) {
      setS(before); // revert
      notify(e?.message || "couldn't save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="kid-card" data-enabled={s.enabled}>
      <div className="kid-card-head">
        <div className="kid-card-id">
          <div className="avatar" style={{ width: 30, height: 30 }}>{kid.kidUserId.slice(0, 1).toUpperCase()}</div>
          <div>
            <div className="kid-card-name">{kid.kidUserId}</div>
            <div className="kid-card-meta">
              {s.enabled ? `${kid.usageToday}${s.dailyLimit != null ? ` / ${s.dailyLimit}` : ""} today` : "chat off"}
              {kid.unreviewedFlags > 0 && <span className="kid-flag-pill">⚠ {kid.unreviewedFlags} flagged</span>}
            </div>
          </div>
        </div>
      </div>

      <div className="kid-card-apps">
        <div className="kid-card-apps-label">Apps for {kid.kidUserId}</div>
        <div className="kid-card-apps-toggles">
          <FeatureChip label="Chat" on={s.enabled} disabled={saving} onToggle={(en) => save({ enabled: en })} />
          <FeatureChip label="Rewards" on={feat.rewards} onToggle={(en) => toggleFeature("rewards", en)} />
          {/* Schedule events render on the kid's Today screen
              unconditionally — no per-kid toggle. */}
          <FeatureChip label="To-do" on={feat.todo} onToggle={(en) => toggleFeature("todo", en)} />
          <FeatureChip label="Journal" on={feat.journal} onToggle={(en) => toggleFeature("journal", en)} />
        </div>
        <div className="kid-card-apps-note">Today &amp; Chores are always on.</div>
      </div>

      {s.enabled && (
        <div className="kid-card-controls">
          <label className="kid-field">
            <span>Daily message limit</span>
            <input
              type="number" min={0} max={1000} placeholder="unlimited"
              value={s.dailyLimit ?? ""}
              onInput={(e) => {
                const v = (e.currentTarget as HTMLInputElement).value.trim();
                patchLocal({ dailyLimit: v === "" ? null : Math.max(0, Math.floor(Number(v)) || 0) });
              }}
              onBlur={(e) => {
                const v = (e.currentTarget as HTMLInputElement).value.trim();
                save({ dailyLimit: v === "" ? null : Math.max(0, Math.floor(Number(v)) || 0) });
              }}
            />
          </label>
          <label className="kid-field">
            <span>Quiet hours</span>
            <span className="kid-quiet">
              <input type="time" value={s.quietStart ?? ""} onChange={(e) => save({ quietStart: (e.currentTarget as HTMLInputElement).value || null })} />
              <span className="kid-quiet-sep">to</span>
              <input type="time" value={s.quietEnd ?? ""} onChange={(e) => save({ quietEnd: (e.currentTarget as HTMLInputElement).value || null })} />
            </span>
          </label>
          <label className="kid-field kid-field-wide">
            <span>Assistant persona</span>
            <input
              type="text" maxLength={300} placeholder="e.g. patient tutor for a 7-year-old"
              value={s.persona ?? ""}
              onInput={(e) => patchLocal({ persona: (e.currentTarget as HTMLInputElement).value || null })}
              onBlur={(e) => { const v = (e.currentTarget as HTMLInputElement).value.trim(); save({ persona: v || null }); }}
            />
          </label>
        </div>
      )}

      <div className="kid-card-foot">
        <button className="btn" type="button" onClick={onReview}>
          <Icon name="activity" size={13} />
          <span>Review chats{kid.unreviewedFlags > 0 ? ` · ${kid.unreviewedFlags} flagged` : ""}</span>
        </button>
      </div>
    </section>
  );
}

function KidReview({ kidUserId, notify, onBack }: { kidUserId: string; notify: NotifyFn; onBack: () => void }) {
  const [sessions, setSessions] = useState<KidChatSessionSummary[] | null>(null);
  const [flags, setFlags] = useState<KidChatFlag[]>([]);
  const [active, setActive] = useState<string | null>(null);
  const [transcript, setTranscript] = useState<{ role: string; content: any }[] | null>(null);

  useEffect(() => {
    api.kidsListChats(kidUserId).then(setSessions).catch((e: any) => { notify(e?.message || "couldn't load chats"); setSessions([]); });
    api.kidsListFlags(kidUserId).then(setFlags).catch(() => setFlags([]));
  }, [kidUserId]);

  const openSession = (id: string) => {
    setActive(id);
    setTranscript(null);
    api.kidsLoadChat(kidUserId, id).then((r) => setTranscript(r ? r.messages : [])).catch(() => setTranscript([]));
  };

  const review = async (flagId: number) => {
    try { await api.kidsReviewFlag(flagId); setFlags((prev) => prev.map((f) => f.id === flagId ? { ...f, reviewedAt: new Date().toISOString() } : f)); }
    catch (e: any) { notify(e?.message || "couldn't update"); }
  };

  const asText = (content: any): string => {
    if (typeof content === "string") return content;
    if (Array.isArray(content)) return content.map((p) => (typeof p === "string" ? p : p?.text || "")).join(" ");
    return "";
  };

  return (
    <div className="kid-review">
      <button className="family-back" type="button" onClick={onBack}>
        <Icon name="arrow-left" size={16} /><span>{kidUserId}'s chats</span>
      </button>

      {flags.length > 0 && (
        <section className="kid-review-flags">
          <div className="kid-review-label">Flagged messages</div>
          {flags.map((f) => (
            <div key={f.id} className="kid-flag-row" data-reviewed={!!f.reviewedAt}>
              {/* "From" pill — tells the parent whether the kid said it
                  or the model did. Output moderation produces 'assistant'
                  flags; input moderation produces 'user' flags. */}
              <span className={`kid-flag-from kid-flag-from-${f.role || "user"}`}>
                {f.role === "assistant" ? "model said" : "kid said"}
              </span>
              <div className="kid-flag-cats">{f.categories.map((c) => <span key={c} className="kid-flag-cat">{c.replace(/_/g, " ")}</span>)}</div>
              <div className="kid-flag-snippet">{f.snippet || "(no preview)"}</div>
              {f.reviewedAt
                ? <span className="kid-flag-done">reviewed</span>
                : <button className="link-btn" type="button" onClick={() => review(f.id)}>Mark reviewed</button>}
            </div>
          ))}
        </section>
      )}

      <div className="kid-review-grid">
        <div className="kid-review-sessions">
          <div className="kid-review-label">Conversations</div>
          {sessions === null && <p className="family-loading">Loading…</p>}
          {sessions !== null && sessions.length === 0 && <p className="kid-review-empty">No chats yet.</p>}
          {sessions?.map((sess) => (
            <button
              key={sess.id}
              className={`kid-session-row${active === sess.id ? " active" : ""}`}
              type="button"
              onClick={() => openSession(sess.id)}
            >
              {sess.name || "Untitled"}
            </button>
          ))}
        </div>
        <div className="kid-review-transcript">
          {active === null && <p className="kid-review-empty">Pick a conversation to read it.</p>}
          {active !== null && transcript === null && <p className="family-loading">Loading…</p>}
          {transcript?.length === 0 && active !== null && <p className="kid-review-empty">(empty)</p>}
          {transcript?.filter((m) => m.role === "user" || m.role === "assistant").map((m, i) => (
            <div key={i} className={`kid-msg kid-msg-${m.role}`}>
              <div className="kid-msg-role">{m.role === "user" ? kidUserId : "assistant"}</div>
              <div className="kid-msg-body">{asText(m.content)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** A big, obvious on/off toggle for a per-kid app surface. Full tap target
 *  with an explicit On/Off state — sized for mobile/tablet thumbs. */
function FeatureChip({ label, on, disabled, onToggle }: {
  label: string; on: boolean; disabled?: boolean; onToggle: (enabled: boolean) => void;
}) {
  return (
    <button
      type="button"
      className="kid-feature-toggle"
      data-on={on}
      disabled={disabled}
      role="switch"
      aria-checked={on}
      aria-label={`${label} ${on ? "on" : "off"}`}
      onClick={() => onToggle(!on)}
    >
      <span className="kid-feature-toggle-label">{label}</span>
      <span className="kid-feature-switch" aria-hidden><span className="kid-feature-knob" /></span>
    </button>
  );
}
